import intl from "react-intl-universal";
import {
  endOfDay,
  formatFilterAmount,
  formatFilterDate,
  parseAmountInput,
  startOfDay,
} from "../../../../Components/Molecules/GridHistory/gridFilterUtils";
import { BAAS_TRANSACTION_TYPES } from "../../../../Services/baasTransactionsApi";

export const EMPTY_BAAS_EXTRATO_FILTERS = {
  searchQuery: "",
  dateFrom: null,
  dateTo: null,
  amountMin: "",
  amountMax: "",
  directions: [],
  types: [],
  currencies: [],
};

/** Initial Extrato filters — Data chip = today (start=end). */
export const createDefaultBaasExtratoFilters = () => {
  const today = startOfDay(new Date());
  return {
    ...EMPTY_BAAS_EXTRATO_FILTERS,
    dateFrom: today,
    dateTo: today,
  };
};

const CURRENCY_ORDER = ["BRL", "USD", "EUR", "GBP", "USDC", "USDT"];

export const getBaasExtratoDirectionOptions = () => [
  { id: "inbound", label: intl.get("extract.typeCredit") },
  { id: "outbound", label: intl.get("extract.typeDebit") },
];

export const getBaasExtratoRailOptions = () =>
  BAAS_TRANSACTION_TYPES.map((type) => ({
    id: type,
    label: intl.get(`baas.transactions.type.${type}`),
  }));

/**
 * Currency chip options from statement rows + optional account balance codes.
 * Prefer account balances so empty-statement currencies still appear.
 */
export const getBaasExtratoCurrencyOptions = (items = [], accountCurrencies = []) => {
  const seen = new Set();

  const add = (code) => {
    const normalized = String(code || "").trim().toUpperCase();
    if (normalized) seen.add(normalized);
  };

  (Array.isArray(accountCurrencies) ? accountCurrencies : []).forEach(add);
  (Array.isArray(items) ? items : []).forEach((item) => add(item?.currency));

  return Array.from(seen)
    .sort((a, b) => {
      const indexA = CURRENCY_ORDER.indexOf(a);
      const indexB = CURRENCY_ORDER.indexOf(b);
      const rankA = indexA === -1 ? CURRENCY_ORDER.length : indexA;
      const rankB = indexB === -1 ? CURRENCY_ORDER.length : indexB;
      if (rankA !== rankB) return rankA - rankB;
      return a.localeCompare(b);
    })
    .map((currency) => ({ id: currency, label: currency }));
};

const matchesSearch = (item, query) => {
  if (!query?.trim()) return true;
  const normalized = query.trim().toLowerCase();
  return [
    item.counterpartyName,
    item.clientName,
    item.description,
    item.name,
    item.operation_id,
    item.type,
    item.rail,
    item.referenceId,
    item.accountId,
    item.category?.name,
  ]
    .filter(Boolean)
    .some((value) => String(value).toLowerCase().includes(normalized));
};

const matchesDate = (item, filters) => {
  if (!filters.dateFrom && !filters.dateTo) return true;
  if (!item.createdAt) return false;
  const date = new Date(item.createdAt);
  if (Number.isNaN(date.getTime())) return false;
  if (filters.dateFrom && date < startOfDay(filters.dateFrom)) return false;
  if (filters.dateTo && date > endOfDay(filters.dateTo)) return false;
  return true;
};

const matchesAmount = (item, filters) => {
  const min = parseAmountInput(filters.amountMin);
  const max = parseAmountInput(filters.amountMax);
  if (min == null && max == null) return true;
  // Extract-shaped items store amount in cents.
  const amountCents = Math.abs(Number(item.amount) || 0);
  if (min != null && amountCents < Math.round(min * 100)) return false;
  if (max != null && amountCents > Math.round(max * 100)) return false;
  return true;
};

const matchesDirection = (item, filters) => {
  if (!filters.directions?.length) return true;
  return filters.directions.includes(item.direction);
};

const matchesRail = (item, filters) => {
  if (!filters.types?.length) return true;
  const rail = String(item.rail || item.type || "").toUpperCase();
  return filters.types.some((type) => String(type).toUpperCase() === rail);
};

const matchesCurrency = (item, filters) => {
  if (!filters.currencies?.length) return true;
  const currency = String(item.currency || "BRL").toUpperCase();
  return filters.currencies.some(
    (selected) => String(selected).toUpperCase() === currency,
  );
};

export const filterBaasExtratoItems = (items, filters) => {
  const list = Array.isArray(items) ? items : [];
  const resolved = filters || EMPTY_BAAS_EXTRATO_FILTERS;

  return list.filter(
    (item) =>
      matchesSearch(item, resolved.searchQuery) &&
      matchesDate(item, resolved) &&
      matchesAmount(item, resolved) &&
      matchesDirection(item, resolved) &&
      matchesRail(item, resolved) &&
      matchesCurrency(item, resolved),
  );
};

export const hasActiveBaasExtratoFilters = (filters) => {
  const resolved = filters || EMPTY_BAAS_EXTRATO_FILTERS;
  return Boolean(
    resolved.searchQuery?.trim() ||
      resolved.dateFrom ||
      resolved.dateTo ||
      resolved.amountMin ||
      resolved.amountMax ||
      resolved.directions?.length ||
      resolved.types?.length ||
      resolved.currencies?.length,
  );
};

export const isBaasExtratoChipFilterActive = (filters, chipId) => {
  const resolved = filters || EMPTY_BAAS_EXTRATO_FILTERS;
  switch (chipId) {
    case "date":
      return Boolean(resolved.dateFrom || resolved.dateTo);
    case "amount":
      return Boolean(resolved.amountMin || resolved.amountMax);
    case "type":
      return Boolean(resolved.types?.length);
    case "direction":
      return Boolean(resolved.directions?.length);
    case "currency":
      return Boolean(resolved.currencies?.length);
    default:
      return false;
  }
};

export const buildBaasExtratoFilterTags = (filters) => {
  const resolved = filters || EMPTY_BAAS_EXTRATO_FILTERS;
  const tags = [];
  const railOptions = getBaasExtratoRailOptions();

  if (resolved.searchQuery?.trim()) {
    tags.push({
      key: "searchQuery",
      value: resolved.searchQuery.trim(),
      iconType: "search",
    });
  }

  if (resolved.dateFrom || resolved.dateTo) {
    const fromLabel = resolved.dateFrom ? formatFilterDate(resolved.dateFrom) : "…";
    const toLabel = resolved.dateTo ? formatFilterDate(resolved.dateTo) : "…";
    tags.push({
      key: "date",
      value: `${fromLabel} – ${toLabel}`,
      iconType: "date",
    });
  }

  if (resolved.amountMin !== "") {
    tags.push({
      key: "amountMin",
      value: formatFilterAmount(resolved.amountMin),
      iconType: "amount",
    });
  }

  if (resolved.amountMax !== "") {
    tags.push({
      key: "amountMax",
      value: formatFilterAmount(resolved.amountMax),
      iconType: "amount",
    });
  }

  (resolved.types || []).forEach((type) => {
    const option = railOptions.find((item) => item.id === type);
    tags.push({
      key: "type",
      value: option?.label || type,
      iconType: "type",
      type,
    });
  });

  (resolved.directions || []).forEach((direction) => {
    const option = getBaasExtratoDirectionOptions().find((item) => item.id === direction);
    tags.push({
      key: "direction",
      value: option?.label || direction,
      iconType: "type",
      direction,
    });
  });

  (resolved.currencies || []).forEach((currency) => {
    tags.push({
      key: "currency",
      value: currency,
      iconType: "currency",
      currency,
    });
  });

  return tags;
};

export const removeBaasExtratoFilterTag = (filters, key, tag) => {
  const next = { ...(filters || EMPTY_BAAS_EXTRATO_FILTERS) };

  switch (key) {
    case "searchQuery":
      next.searchQuery = "";
      break;
    case "date":
      next.dateFrom = null;
      next.dateTo = null;
      break;
    case "amountMin":
      next.amountMin = "";
      break;
    case "amountMax":
      next.amountMax = "";
      break;
    case "type":
      next.types = (next.types || []).filter((type) => type !== tag?.type);
      break;
    case "direction":
      next.directions = (next.directions || []).filter(
        (direction) => direction !== tag?.direction,
      );
      break;
    case "currency":
      next.currencies = (next.currencies || []).filter(
        (currency) => currency !== tag?.currency,
      );
      break;
    default:
      break;
  }

  return next;
};
